import { LegalPageLayout, type LegalSection } from '../ui/LegalPageLayout';

const EFFECTIVE_DATE = '11 July 2026';

const INTRO =
  'DocCarePlus ("we", "us", or "our") provides clinic management software for healthcare providers and their patients. This Privacy Policy explains what information we collect, how we use it, and the choices you have. By using DocCarePlus you agree to the practices described here.';

const SECTIONS: LegalSection[] = [
  {
    heading: 'Information We Collect',
    paragraphs: ['We collect information you provide and information generated while you use the service, including:'],
    bullets: [
      'Account details — name, clinic name, email address, and phone number used to register and sign in.',
      'Clinical and operational data — patient records, appointments, prescriptions, invoices, and related notes entered by clinic staff.',
      'Usage and device data — log information, IP address, browser type, and interactions used to keep the service secure and reliable.',
    ],
  },
  {
    heading: 'How We Use Your Information',
    paragraphs: ['We use the information we collect to:'],
    bullets: [
      'Provide, maintain, and improve the DocCarePlus platform.',
      'Authenticate users, secure accounts, and prevent fraud or abuse.',
      'Send service-related communications such as verification codes, receipts, and important updates.',
      'Comply with legal obligations and enforce our Terms of Service.',
    ],
  },
  {
    heading: 'Health Data & Confidentiality',
    paragraphs: [
      'Patient health information entered into DocCarePlus belongs to the clinic that created it. We act as a processor of that data on the clinic’s behalf and access it only as needed to operate, support, and secure the service.',
      'We do not sell health data, and we do not use it for advertising. Access within our organisation is limited to authorised personnel bound by confidentiality obligations.',
    ],
  },
  {
    heading: 'Sharing & Disclosure',
    paragraphs: ['We share information only in limited circumstances:'],
    bullets: [
      'With service providers who host, process, or support the platform under contractual confidentiality and security obligations.',
      'When required by law, regulation, legal process, or an enforceable governmental request.',
      'To protect the rights, safety, and security of our users, the public, or DocCarePlus.',
    ],
  },
  {
    heading: 'Data Security',
    paragraphs: [
      'We use industry-standard safeguards including encryption in transit, access controls, and monitoring to protect your information. No method of transmission or storage is completely secure, but we work continuously to protect your data and promptly address vulnerabilities.',
    ],
  },
  {
    heading: 'Data Retention',
    paragraphs: [
      'We retain information for as long as your account is active or as needed to provide the service, comply with legal obligations, resolve disputes, and enforce our agreements. Clinics may request export or deletion of their data subject to applicable law.',
    ],
  },
  {
    heading: 'Your Rights',
    paragraphs: [
      'Depending on your jurisdiction, you may have the right to access, correct, export, or delete your personal information, and to object to or restrict certain processing. To exercise these rights, contact us using the details below. Patients should direct requests about their clinical records to their clinic, which controls that data.',
    ],
  },
  {
    heading: 'Cookies & Tracking',
    paragraphs: [
      'We use cookies and similar technologies to keep you signed in, remember preferences, and understand how the service is used. You can control cookies through your browser settings, though some features may not function without them.',
    ],
  },
  {
    heading: 'Children’s Privacy',
    paragraphs: [
      'DocCarePlus is intended for use by healthcare providers and adult account holders. Patient records for minors are managed by clinics under their own legal responsibilities. We do not knowingly create accounts for children.',
    ],
  },
  {
    heading: 'Changes to This Policy',
    paragraphs: [
      'We may update this Privacy Policy from time to time. When we make material changes, we will update the "Last updated" date above and, where appropriate, notify you through the service.',
    ],
  },
  {
    heading: 'Contact Us',
    paragraphs: [
      'If you have questions about this Privacy Policy or how we handle your data, contact us at hello@doccareplus.com.',
    ],
  },
];

export function PrivacyPolicyPage() {
  return (
    <LegalPageLayout
      title="Privacy Policy"
      effectiveDate={EFFECTIVE_DATE}
      intro={INTRO}
      sections={SECTIONS}
    />
  );
}
